/**
 * Rainbow Mulch — Playground Project Builder
 * LIVE FREIGHT CLIENT (Sprint 9 — replaces PBMocks.fetchFreight)
 *
 * ============================================================================
 *  WHAT THIS DOES
 * ============================================================================
 * Calls the backend through the Shopify App Proxy at
 * `PBConfig.freight.quoteEndpoint`, which Shopify forwards to the Cloudflare
 * Worker (D-039) after signing it. The Worker calls R+L (D-041), and returns a
 * quote or — when the carrier cannot be reached — the fitted fallback range
 * (D-042).
 *
 * ============================================================================
 *  SH-18 — WHY THIS SENDS ONLY RAW INPUTS
 * ============================================================================
 * The request body carries dimensions, depth, packaging, ZIP and delivery
 * options. It deliberately carries NO weight, NO volume and NO price.
 *
 * The server recomputes all of those from the raw inputs using the same rules
 * the storefront uses (backend/src/shared-rules.js). If the browser could send
 * a weight or an amount, a customer could edit an $800 charge to $8 — so the
 * shape of this payload is itself the safeguard. Anything added here that looks
 * like a derived quantity is a security regression, not a convenience.
 *
 * The server ignores extra fields regardless; this was verified against the
 * deployed Worker by posting `amount: 8, weightLbs: 1`, which still returned
 * the correctly recomputed shipment (D-043).
 *
 * ============================================================================
 *  FAILURE IS A FIRST-CLASS PATH, NOT AN EXCEPTION
 * ============================================================================
 * D-004: a carrier outage must never block the customer, and must never
 * silently invent a number. Every CLIENT-SIDE failure mode below (the server
 * was never reached, or its response could not be trusted) resolves to
 * `status: 'estimated'` with a `reason`, so Step 4 can show the fitted range
 * and a specialist referral rather than an error dialog:
 *
 *   - network error / offline
 *   - timeout (SH-12, PBConfig.freight.timeoutMs)
 *   - non-2xx from the proxy (401 unsigned, 404 misrouted, 5xx)
 *   - unparseable JSON
 *   - a 200 whose payload is missing the amount
 *
 * A genuine 200 from the server carries its OWN status the server chose —
 * `quoted`, the D-004 `estimated` fallback, or (D-070) `oversized` for a
 * shipment over R+L's weight ceiling — see interpret() below.
 *
 * This function therefore NEVER rejects. Callers do not need a .catch().
 */

window.PBFreightClient = (function () {
  'use strict';

  /**
   * Normalise every outcome into the shape the builder's state machine expects:
   *   { status: 'quoted'|'estimated', amount, transitDays, range, reason }
   */
  function estimated(reason) {
    return { status: 'estimated', amount: null, transitDays: null, reason: reason };
  }

  /**
   * Test override, retained deliberately after the mock was removed.
   *
   * `?pb_mock_freight=fail` forces the D-004 path so the fallback state stays
   * reviewable without having to break the carrier or invent a bad ZIP. It only
   * ever forces a FAILURE — it cannot fabricate a quote, so it cannot be used to
   * manufacture a price. `?pb_mock_freight=slow` exercises the loading state.
   */
  function override() {
    try {
      return new URLSearchParams(window.location.search).get('pb_mock_freight');
    } catch (e) {
      return null;
    }
  }

  /**
   * Request a freight quote.
   *
   * @param {object} state the builder state
   * @returns {Promise<{status:string, amount:?number, transitDays:?number,
   *                    range:?object, reason:?string}>} never rejects
   */
  function fetchFreight(state) {
    var cfg = window.PBConfig;
    var forced = override();

    if (forced === 'fail') {
      return delay(600).then(function () { return estimated('forced_failure_override'); });
    }

    var endpoint = cfg.freight.quoteEndpoint;
    if (!endpoint) {
      // Configuration error, not a carrier problem — but the customer-facing
      // behaviour is the same, so it still routes to the estimated path.
      return Promise.resolve(estimated('endpoint_not_configured'));
    }

    /**
     * SH-18 — raw inputs ONLY. See the header. Sending `packaging` is safe: it
     * selects a config entry server-side, it does not carry a price.
     */
    var body = {
      shape: state.shape,
      length: state.length,
      width: state.width,
      diameter: state.diameter,
      depth: state.depth,
      packaging: state.packaging,
      zip: state.zip,
      deliveryType: state.deliveryType,
      /**
       * Item 11 (2026-09-11) — liftgate is included on every real freight
       * quote unconditionally, regardless of delivery type. Hardcoded here
       * rather than read from state.liftgate: this function is the one place
       * SH-18 already trusts to shape exactly what a live quote request
       * contains, so it is the right place to GUARANTEE this too, rather
       * than relying on state.liftgate never drifting from 'yes' elsewhere.
       */
      liftgate: true
    };

    // SH-12 — an explicit timeout. Without it a hung carrier call would leave
    // the customer on a spinner indefinitely, which is worse than an estimate.
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timedOut = false;
    var timer = setTimeout(function () {
      timedOut = true;
      if (controller) controller.abort();
    }, cfg.freight.timeoutMs);

    var opts = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      // Same-origin through the App Proxy, so Shopify signs it and the session
      // cookie travels. Without this the Worker sees an unsigned request.
      credentials: 'same-origin'
    };
    if (controller) opts.signal = controller.signal;

    var slow = forced === 'slow' ? delay(3000) : Promise.resolve();

    return slow
      .then(function () { return fetch(endpoint, opts); })
      .then(function (res) {
        clearTimeout(timer);
        if (!res.ok) {
          // 401 means the proxy signature was rejected — a configuration fault
          // worth distinguishing in the reason, since it is not a carrier issue.
          return estimated('http_' + res.status);
        }
        return res.json().then(function (data) {
          return interpret(data);
        }, function () {
          return estimated('unparseable_response');
        });
      })
      .catch(function (err) {
        clearTimeout(timer);
        if (timedOut) return estimated('timeout');
        return estimated(err && err.name === 'AbortError' ? 'aborted' : 'network_error');
      });
  }

  /**
   * Turn a 200 response into state.
   *
   * A quote is only trusted when the server says `quoted` AND supplies a finite
   * positive amount. A 200 carrying a null or zero amount is treated as a
   * failure, not as free shipping — the same class of bug as `parseRLMoney`
   * once returning 0 for 'n/a' (D-041).
   */
  function interpret(data) {
    if (!data || typeof data !== 'object') return estimated('empty_response');

    if (data.status === 'quoted') {
      var amt = typeof data.amount === 'number' ? data.amount : Number(data.amount);
      if (!isFinite(amt) || amt <= 0) return estimated('quote_missing_amount');
      return {
        status: 'quoted',
        amount: amt,
        transitDays: typeof data.transitDays === 'number' ? data.transitDays : null,
        serviceCode: data.serviceCode || null,
        // Item 1 (2026-09-11) — defense in depth only: the builder's own
        // fetchFreight() already short-circuits flat-fee orders before this
        // function is ever called (no network round trip at all), so this
        // branch is reachable only if that check and the server's ever
        // disagreed. Carried through here anyway so the UI still shows
        // honest carrier copy rather than defaulting to "R+L Carriers" if so.
        flatFee: data.serviceCode === 'FLAT_UPS',
        range: null,
        reason: null
      };
    }

    /**
     * D-070 — a shipment over R+L's 16,000 lb quotable ceiling. Kept as its
     * own branch (not folded into the generic `estimated` case below): this
     * is a known, permanent limit, not a carrier outage, so it gets its own
     * `status` rather than being an `estimated` response a caller has to
     * further inspect a `reason` string on to tell apart from a real D-042
     * fallback range. `range` is always null here by construction.
     */
    if (data.status === 'oversized') {
      return {
        status: 'oversized',
        amount: null,
        transitDays: null,
        range: null,
        requiresManualConfirmation: data.requiresManualConfirmation === true,
        reason: data.reason || 'exceeds_ltl_weight_limit'
      };
    }

    /**
     * Item 12 (2026-09-11) — a ZIP R+L does not recognize, kept as its own
     * branch for the same reason `oversized` is: a customer-fixable, distinct
     * condition, not the generic D-004 carrier-outage estimate below. No
     * range at all (unlike `estimated`) — the builder's own state machine
     * (fetchFreight in pb-builder.js) uses this to prompt a ZIP re-entry, and
     * escalates to a specialist/save referral after a second consecutive one.
     */
    if (data.status === 'invalid_zip') {
      return { status: 'invalid_zip', amount: null, transitDays: null, range: null, reason: null };
    }

    // Server-side estimated path (D-004 carrier-failure fallback). It may
    // carry the fitted range (D-042), which may itself be null for an
    // unrecognised ZIP or a non-LTL lane — but never for the over-weight
    // case, which is handled above and never reaches here.
    return {
      status: 'estimated',
      amount: null,
      transitDays: null,
      range: data.range || null,
      requiresManualConfirmation: data.requiresManualConfirmation === true,
      reason: data.reason || 'carrier_unavailable'
    };
  }

  function delay(ms) {
    return new Promise(function (r) { setTimeout(r, ms); });
  }

  return { fetchFreight: fetchFreight, interpret: interpret };
})();
